<template>
  <div class="page-container">
    <div class="page-header">
      <h2>Cadastro dos Programas de Autocontrole do Estabelecimento</h2>
      <p>
        Cadastro dos programas de autocontrole vinculados ao estabelecimento,
        utilizados como base para acompanhamento, inspeções e auditorias do SIM.
      </p>
    </div>

    <!-- FORMULÁRIO -->
    <section class="card form-card">
      <div class="card-title">
        <h3>{{ editando ? 'Editar Programa de Autocontrole' : 'Novo Programa de Autocontrole' }}</h3>
      </div>

      <div class="form-grid">
        <div class="form-group span-2">
          <label>Estabelecimento</label>
          <select v-model="form.idEstabelecimento">
            <option :value="null">Selecione</option>
            <option
              v-for="item in lsEstabelecimentos"
              :key="item.id"
              :value="item.id"
            >
              {{ item.nome }}
            </option>
          </select>
        </div>

        <div class="form-group span-2">
          <label>Programa de Autocontrole</label>
          <input
            v-model="form.nmPrograma"
            type="text"
            placeholder="Ex.: Boas Práticas de Fabricação"
          />
        </div>

        <div class="form-group">
          <label>Classificação</label>
          <select v-model="form.classificacao">
            <option value="">Selecione</option>
            <option value="BPF">BPF</option>
            <option value="HIGIENE">Higiene</option>
            <option value="AGUA">Controle de Água</option>
            <option value="PRAGAS">Controle de Pragas</option>
            <option value="RASTREABILIDADE">Rastreabilidade</option>
            <option value="MANUTENCAO">Manutenção / Calibração</option>
            <option value="TEMPERATURA">Controle de Temperatura</option>
            <option value="RESIDUOS">Resíduos</option>
            <option value="MANIPULADORES">Manipuladores</option>
            <option value="OUTROS">Outros</option>
          </select>
        </div>

        <div class="form-group">
          <label>Situação</label>
          <select v-model="form.situacao">
            <option value="">Selecione</option>
            <option value="IMPLANTADO">Implantado</option>
            <option value="EM_IMPLANTACAO">Em implantação</option>
            <option value="NAO_IMPLANTADO">Não implantado</option>
            <option value="SUSPENSO">Suspenso</option>
          </select>
        </div>

        <div class="form-group">
          <label>Responsável</label>
          <input
            v-model="form.responsavel"
            type="text"
            placeholder="Nome do responsável"
          />
        </div>

        <div class="form-group">
          <label>Periodicidade</label>
          <select v-model="form.periodicidade">
            <option value="">Selecione</option>
            <option value="DIARIA">Diária</option>
            <option value="SEMANAL">Semanal</option>
            <option value="QUINZENAL">Quinzenal</option>
            <option value="MENSAL">Mensal</option>
            <option value="BIMESTRAL">Bimestral</option>
            <option value="TRIMESTRAL">Trimestral</option>
            <option value="SEMESTRAL">Semestral</option>
            <option value="ANUAL">Anual</option>
            <option value="SOB_DEMANDA">Sob demanda</option>
          </select>
        </div>

        <div class="form-group">
          <label>Data de Implantação</label>
          <input v-model="form.dtImplantacao" type="date" />
        </div>

        <div class="form-group checkbox-group">
          <label>Exige registro documental?</label>
          <div class="checkbox-inline">
            <input v-model="form.exigeRegistro" type="checkbox" />
            <span>Sim</span>
          </div>
        </div>

        <div class="form-group span-4">
          <label>Observações</label>
          <textarea
            v-model="form.observacoes"
            rows="3"
            placeholder="Informações complementares sobre o programa..."
          ></textarea>
        </div>
      </div>

      <div class="form-actions">
        <button class="btn btn-primary" @click="salvarRegistro">
          {{ editando ? 'Salvar Alterações' : 'Adicionar' }}
        </button>
        <button class="btn btn-secondary" @click="limparForm">
          Limpar
        </button>
      </div>
    </section>

    <!-- LISTA -->
    <section class="card table-card">
      <div class="card-title card-title-row">
        <h3>Programas Cadastrados</h3>
        <span class="badge">{{ lsProgramas.length }} registro(s)</span>
      </div>

      <div class="table-responsive">
        <table class="data-table">
          <thead>
            <tr>
              <th>Estabelecimento</th>
              <th>Programa</th>
              <th>Classificação</th>
              <th>Situação</th>
              <th>Responsável</th>
              <th>Periodicidade</th>
              <th>Implantação</th>
              <th>Registro</th>
              <th style="width: 150px;">Ações</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="lsProgramas.length === 0">
              <td colspan="9" class="empty-cell">
                Nenhum programa de autocontrole cadastrado.
              </td>
            </tr>

            <tr v-for="item in lsProgramas" :key="item.id">
              <td>{{ getNomeEstabelecimento(item.idEstabelecimento) }}</td>
              <td>{{ item.nmPrograma }}</td>
              <td>{{ item.classificacao }}</td>
              <td>
                <span class="status-pill" :class="getStatusClass(item.situacao)">
                  {{ formatarSituacao(item.situacao) }}
                </span>
              </td>
              <td>{{ item.responsavel }}</td>
              <td>{{ formatarPeriodicidade(item.periodicidade) }}</td>
              <td>{{ formatarData(item.dtImplantacao) }}</td>
              <td>{{ item.exigeRegistro ? 'Sim' : 'Não' }}</td>
              <td>
                <div class="table-actions">
                  <button class="btn-action edit" @click="editarRegistro(item)">Editar</button>
                  <button class="btn-action remove" @click="removerRegistro(item.id)">Excluir</button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>
  </div>
</template>

<script setup>
import { reactive, ref } from 'vue'

const lsEstabelecimentos = ref([
  { id: 1, nome: 'Frigorífico Modelo LTDA' },
  { id: 2, nome: 'Laticínios Serra Azul' },
  { id: 3, nome: 'Entreposto de Mel Vale Doce' },
])

const novoRegistro = () => ({
  id: null,
  idEstabelecimento: null,
  nmPrograma: '',
  classificacao: '',
  situacao: '',
  responsavel: '',
  periodicidade: '',
  dtImplantacao: '',
  exigeRegistro: true,
  observacoes: '',
})

const form = reactive(novoRegistro())
const editando = ref(false)

const lsProgramas = ref([
  {
    id: 1,
    idEstabelecimento: 1,
    nmPrograma: 'Boas Práticas de Fabricação',
    classificacao: 'BPF',
    situacao: 'IMPLANTADO',
    responsavel: 'Carlos Mendes',
    periodicidade: 'DIARIA',
    dtImplantacao: '2026-01-15',
    exigeRegistro: true,
    observacoes: 'Programa implantado com registros diários de verificação.',
  },
  {
    id: 2,
    idEstabelecimento: 2,
    nmPrograma: 'Controle de Água de Abastecimento',
    classificacao: 'AGUA',
    situacao: 'EM_IMPLANTACAO',
    responsavel: 'Marina Lopes',
    periodicidade: 'MENSAL',
    dtImplantacao: '2026-02-10',
    exigeRegistro: true,
    observacoes: 'Aguardando consolidação dos laudos periódicos.',
  },
  {
    id: 3,
    idEstabelecimento: 3,
    nmPrograma: 'Controle Integrado de Pragas',
    classificacao: 'PRAGAS',
    situacao: 'IMPLANTADO',
    responsavel: 'João Pereira',
    periodicidade: 'MENSAL',
    dtImplantacao: '2025-11-20',
    exigeRegistro: true,
    observacoes: 'Acompanhamento terceirizado com registros mensais.',
  },
])

function limparForm() {
  Object.assign(form, novoRegistro())
  editando.value = false
}

function validarForm() {
  if (!form.idEstabelecimento) {
    alert('Selecione o estabelecimento.')
    return false
  }

  if (!form.nmPrograma.trim()) {
    alert('Informe o nome do programa de autocontrole.')
    return false
  }

  if (!form.classificacao) {
    alert('Selecione a classificação.')
    return false
  }

  if (!form.situacao) {
    alert('Selecione a situação.')
    return false
  }

  return true
}

function salvarRegistro() {
  if (!validarForm()) return

  if (editando.value) {
    const index = lsProgramas.value.findIndex(item => item.id === form.id)
    if (index !== -1) {
      lsProgramas.value[index] = { ...form }
    }
  } else {
    lsProgramas.value.unshift({
      ...form,
      id: Date.now(),
    })
  }

  limparForm()
}

function editarRegistro(item) {
  Object.assign(form, { ...item })
  editando.value = true
  window.scrollTo({ top: 0, behavior: 'smooth' })
}

function removerRegistro(id) {
  const ok = confirm('Deseja realmente excluir este registro?')
  if (!ok) return

  lsProgramas.value = lsProgramas.value.filter(item => item.id !== id)

  if (form.id === id) {
    limparForm()
  }
}

function getNomeEstabelecimento(id) {
  const item = lsEstabelecimentos.value.find(e => e.id === id)
  return item ? item.nome : ''
}

function formatarSituacao(valor) {
  const mapa = {
    IMPLANTADO: 'Implantado',
    EM_IMPLANTACAO: 'Em implantação',
    NAO_IMPLANTADO: 'Não implantado',
    SUSPENSO: 'Suspenso',
  }
  return mapa[valor] || valor
}

function formatarPeriodicidade(valor) {
  const mapa = {
    DIARIA: 'Diária',
    SEMANAL: 'Semanal',
    QUINZENAL: 'Quinzenal',
    MENSAL: 'Mensal',
    BIMESTRAL: 'Bimestral',
    TRIMESTRAL: 'Trimestral',
    SEMESTRAL: 'Semestral',
    ANUAL: 'Anual',
    SOB_DEMANDA: 'Sob demanda',
  }
  return mapa[valor] || valor
}

function formatarData(data) {
  if (!data) return ''
  const [ano, mes, dia] = data.split('-')
  return `${dia}/${mes}/${ano}`
}

function getStatusClass(status) {
  if (status === 'IMPLANTADO') return 'status-ok'
  if (status === 'EM_IMPLANTACAO') return 'status-warn'
  if (status === 'NAO_IMPLANTADO') return 'status-danger'
  if (status === 'SUSPENSO') return 'status-muted'
  return ''
}
</script>

<style scoped>
.page-container {
  padding: 20px;
  background: #f5f7fb;
}

.page-header {
  margin-bottom: 20px;
}

.page-header h2 {
  margin: 0 0 6px 0;
  font-size: 24px;
  color: #1f2937;
}

.page-header p {
  margin: 0;
  color: #6b7280;
}

.card {
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 18px;
  margin-bottom: 20px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.03);
}

.card-title {
  margin-bottom: 16px;
}

.card-title h3 {
  margin: 0;
  font-size: 18px;
  color: #111827;
}

.card-title-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.badge {
  background: #eef2ff;
  color: #4338ca;
  padding: 6px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 14px;
}

.form-group {
  display: flex;
  flex-direction: column;
}

.form-group label {
  margin-bottom: 6px;
  font-size: 13px;
  font-weight: 600;
  color: #374151;
}

.form-group input,
.form-group select,
.form-group textarea {
  border: 1px solid #d1d5db;
  border-radius: 8px;
  padding: 10px 12px;
  font-size: 14px;
  background: #fff;
  outline: none;
}

.form-group input:focus,
.form-group select:focus,
.form-group textarea:focus {
  border-color: #2563eb;
}

.span-2 {
  grid-column: span 2;
}

.span-4 {
  grid-column: span 4;
}

.checkbox-group {
  justify-content: flex-end;
}

.checkbox-inline {
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 42px;
}

.form-actions {
  margin-top: 18px;
  display: flex;
  gap: 10px;
}

.btn {
  border: none;
  border-radius: 8px;
  padding: 10px 16px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
}

.btn-primary {
  background: #2563eb;
  color: #fff;
}

.btn-secondary {
  background: #e5e7eb;
  color: #111827;
}

.table-responsive {
  overflow-x: auto;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
}

.data-table th {
  background: #f9fafb;
  color: #374151;
  text-align: left;
  font-size: 13px;
  padding: 12px 10px;
  border-bottom: 1px solid #e5e7eb;
}

.data-table td {
  padding: 12px 10px;
  border-bottom: 1px solid #f1f5f9;
  font-size: 14px;
  color: #1f2937;
  vertical-align: top;
}

.empty-cell {
  text-align: center;
  color: #6b7280;
  padding: 18px;
}

.table-actions {
  display: flex;
  gap: 8px;
}

.btn-action {
  border: none;
  background: transparent;
  cursor: pointer;
  font-weight: 600;
  font-size: 13px;
}

.btn-action.edit {
  color: #2563eb;
}

.btn-action.remove {
  color: #dc2626;
}

.status-pill {
  display: inline-block;
  padding: 5px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
}

.status-ok {
  background: #dcfce7;
  color: #166534;
}

.status-warn {
  background: #fef3c7;
  color: #92400e;
}

.status-danger {
  background: #fee2e2;
  color: #991b1b;
}

.status-muted {
  background: #e5e7eb;
  color: #374151;
}

@media (max-width: 1100px) {
  .form-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .span-4 {
    grid-column: span 2;
  }
}

@media (max-width: 700px) {
  .form-grid {
    grid-template-columns: 1fr;
  }

  .span-2,
  .span-4 {
    grid-column: span 1;
  }

  .form-actions {
    flex-direction: column;
  }
}
</style>